<script setup>
import {Head, router, usePage} from '@inertiajs/vue3';
import StoreLayout from '@/Layouts/StoreLayout.vue';
import Highlight from "@/Pages/Collection/Partials/Highlight.vue";
import Grid from "@/Pages/Product/Partials/Grid.vue";
import Browse from "@/Pages/Product/Partials/Browse.vue";
import {computed, inject, onBeforeMount, ref, watch} from "vue";
import {event} from "vue-gtag";
import Empty from "@/Components/Empty.vue";
import {PhCircleDashed, PhClockCounterClockwise, PhHeart} from '@phosphor-icons/vue'
import ShopLink from "@/Components/ShopLink.vue";
import {productAsAnalyticItem} from "@/mixins";

const route = inject('route');

const props = defineProps({
    pageTitle: String | null,
    title: String,
    collection: Object,
    buyAll: Object,
    products: Object,
    filters: Object,
    sortOptions: Object,
    activeFilters: Object,
});

const gridSize = ref(0);

const { url } = usePage();

onBeforeMount(() => {
    let storedSize = parseInt(localStorage.getItem('lastGridSize') ?? '0');
    if (!isNaN(storedSize)) {
        gridSize.value = storedSize;
    }
});

watch(gridSize, (newSize) => {
    localStorage.setItem('lastGridSize', newSize.toString());
});

function buyAllFabric() {
    router.patch(route('cart.fabric-line', props.buyAll.id));
}

const gaProducts = computed(() => props.products.data.map((product) => product.id));

watch(gaProducts, (newGaProducts, oldGaProducts) => {
    if (JSON.stringify(newGaProducts) !== JSON.stringify(oldGaProducts)) {
        setTimeout(() => {
            event('view_item_list', {
                item_list_name: props.title,
                items: props.products.data.map((product, i) => {
                    return productAsAnalyticItem(product, i);
                }),
            });
        }, 100);
    }
}, {immediate: true});

watch(() => props.activeFilters, (newActiveFilters, oldActiveFilters) => {
    if (JSON.stringify(newActiveFilters) !== JSON.stringify(oldActiveFilters)) {
        setTimeout(() => {
            newActiveFilters.forEach((filter) => {
                const activeFilter = {
                    page_title: props.pageTitle ?? props.title,
                    page_location: url,
                    filter_type: filter.label,
                    filter_value: filter.value,
                    collection: props.collection?.name,
                    page: props.products.current_page,
                };
                event('browse_filter', activeFilter);
            });
        }, 100);
    }
}, {immediate: true});

</script>

<template>
    <Head :title="pageTitle ?? title"/>

    <StoreLayout>
        <Browse :title="title" :filters="filters" :sortOptions="sortOptions" :active-filters="activeFilters"
                v-model:grid-size="gridSize" :total="products.total" :initial-show-filters="collection === null">
            <Highlight :collection="collection" v-if="collection">
                <button
                    v-if="buyAll"
                    @click="buyAllFabric"
                    class="mt-5 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                    Add 1/2 yard of available {{ buyAll.name }} fabric to cart
                </button>
            </Highlight>
            <Grid v-if="products.data.length" :products="products" :gridSize="gridSize"/>
            <div v-else class="max-w-4xl mx-auto px-6 py-12 lg:px-0">
                <Empty v-if="route().current('products.favorites')"
                       :icon="PhHeart"
                       title="No favorites yet"
                       description="Heart products as you browse to save them here.">
                    <ShopLink :href="route('products.browse')">Start browsing</ShopLink>
                </Empty>
                <Empty v-else-if="route().current('products.recent')"
                       :icon="PhClockCounterClockwise"
                       title="Nothing here yet"
                       description="Products you view will appear here.">
                    <ShopLink :href="route('products.browse')">Start browsing</ShopLink>
                </Empty>
                <Empty v-else
                       :icon="PhCircleDashed"
                       title="No results"
                       description="There aren't any products matching your criteria.">
                    <ShopLink :href="route('products.browse')">Browse all</ShopLink>
                </Empty>
            </div>
        </Browse>
    </StoreLayout>
</template>
